import AxiosInstance from 'config/apiServiceApiGateway'
import type { InvoiceFileDownloadTypeIdType } from 'features/Enterprise/Vendor/pages/VendorInvoice/utils/invoiceDocumentDownload'

export interface GetVendorInvoiceQueryType {
  effective_submission_date_start?: string
  effective_submission_date_end?: string
  search_type?: string
  search_value?: string
  order_by?: string
  order_direction?: string
  payment_term?: string
  statuses?: string
  cursor?: string
  page_size?: string
  due_date_time?: number
  po_number_search?: string
}

export enum InvoiceStatusEnum {
  PENDING = 'PENDING',
  REVIEWED = 'REVIEWED',
  APPROVED = 'APPROVED',
  NEED_REVISED = 'NEED_REVISED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
  PAID = 'PAID',
}

export type InvoiceStatusType = `${InvoiceStatusEnum}`

export interface VendorInvoiceInterface {
  id: number
  vendor: {
    name: string
    payment_term: string
  }
  invoice_number: string
  invoice_type: string
  amount: string
  receipt_number: string
  status: InvoiceStatusType
  due_date: string
  created_at: string
  po_numbers?: string[]
  vendor_total_amount?: number
  effective_submission_date?: string
  discrepancy_percentage?: number
}

export interface VendorInvoicePaginationInterface {
  next_cursor: string
  prev_cursor: string
}

export interface VendorInvoiceResponseInterface {
  data: { invoices: VendorInvoiceInterface[] }
  pagination: VendorInvoicePaginationInterface
}

export type InvoiceActivityType =
  | 'documents'
  | 'attachments'
  | 'rejected_reason'
  | 'change_due_date_reason'

export type InvoiceType = 'reguler_invoice' | 'proforma_invoice'

export type InvoiceDocumentKindType =
  | 'proforma_invoice'
  | 'invoice'
  | 'faktur_pajak'
  | 'surat_jalan'
  | 'po'
  | 'grn'
  | 'payment_evidence'
  | 'confirmation_letter'

export type ConfirmationLetterStatusType = 'REQUIRED' | 'NOT_REQUIRED' | 'UPLOADED'

export type DiscrepancyCauseType = 'VENDOR' | 'INTERNAL_ASTRO' | ''

export type AttachmentRejectionTypeType =
  | 'invoice'
  | 'proforma_invoice'
  | 'faktur_pajak'
  | 'surat_jalan'
  | 'grn'
  | 'confirmation_letter'

export interface RevisionHistoryType {
  id: number
  timestamp: string
  user_email: string
  action: string
  po_remarks: string
}

export interface InvoiceActivityItemType {
  status: string
  timestamp: string
  pic_name: string
  reasons: string[]
}

export type SkuMatchingStatusType = 'SUCCESS' | 'PROCESSING' | 'FAILED'

export interface SkuMatchingMatchType {
  invoice_item_id: number
  po_item_ids: number[]
}

export interface SkuMatchingResultType {
  request_id: string
  status: SkuMatchingStatusType
  matches: SkuMatchingMatchType[]
  error_message?: string
}

export interface TriggerSkuMatchingResponseType {
  request_id: string
}

/**
 * Error envelope returned by Commercial-BE when the `ai-be` (AI Gateway) call fails.
 * The backend still responds HTTP 200 with `data: null` and this `error` object rather
 * than an HTTP 500.
 */
export interface AiGatewayFallbackErrorType {
  status: boolean
  message: string
  code: number
}

export interface TriggerSkuMatchingApiResponseType {
  data: TriggerSkuMatchingResponseType | null
  error?: AiGatewayFallbackErrorType
}

export interface GetSkuMatchingResultApiResponseType {
  data: SkuMatchingResultType | null
  error?: AiGatewayFallbackErrorType
}

export interface PODetailsInInvoiceType {
  id: number
  invoice_purchase_order_id?: number
  po_number: string
  purchase_order_id: number
  amount: string
  destination: {
    id: number
    name: string
  }
  created_at: string
  sent_at: string
  vendor_total_amount?: number
  total_price_invoice?: number
  total_price_po?: number
  po_amount_excl_tax?: number
  po_tax_total?: number
  invoice_amount_excl_tax?: number
  invoice_tax_total?: number
  po_total_quantity?: number
  invoice_total_quantity?: number
  total_difference?: number
  discrepancy_percentage?: number
  discrepancy_cause?: string
  po_remark?: string
  po_remark_reason_id?: number
  po_remark_reason?: string
  is_flagged_for_rejection?: boolean
  rejection_reason_summary?: string
  confirmation_letter_status?: ConfirmationLetterStatusType
}

export interface InvoiceDocumentInterface {
  id: number
  document_type: InvoiceDocumentKindType
  file_path: string
  rejection_reason_id?: number
  rejection_reason_text?: string
}

export interface InvoiceDetailInterface {
  invoice_id: number
  invoice_number: string
  invoice_type: InvoiceType
  amount: string
  receipt_number: string
  status: InvoiceStatusType
  due_date: string
  is_invoice_grn_match: boolean
  created_at: string
  vendor: {
    id: number
    name: string
  }
  purchase_orders: PODetailsInInvoiceType[]
  documents: InvoiceDocumentInterface[]
  activities: {
    type: InvoiceActivityType
    notes: string[]
  }[]
  pic: {
    name: string
  }
  issued_date: string
  tax_invoice_date: string
  tax_invoice_number: string
  vendor_total_amount?: number
  ocr_total_amount?: number
  last_approver_name?: string
  revision_count?: number
  last_revision_date?: string
  effective_submission_date?: string
  has_confirmation_letter?: boolean
  has_unmatched_skus?: boolean
  has_price_discrepancy?: boolean
  confirmation_letter_url?: string
  total_po_amount?: number
  total_invoice_amount?: number
  total_difference?: number
  revision_reasons?: string[]
  revision_types?: string[]
  rejection_reason?: string
  revision_histories?: RevisionHistoryType[]
  is_ocr_success?: boolean
  grn_uploaded_count?: number
  grn_expected_count?: number
}

export interface GetInvoiceDetailsResponseInterface {
  data: InvoiceDetailInterface
}

export type InvoicePoMatchingItemPricingType = {
  unit_price: number
  quantity: number
  tax_multiplier: number
  total_price: number
}

export type InvoicePoMatchingInvoiceItemType = InvoicePoMatchingItemPricingType & {
  invoice_po_item_id: number
  vendor_sku_code: string
  vendor_sku_name: string
  po_item_ids: number[]
}

export type InvoicePoMatchingPoItemType = InvoicePoMatchingItemPricingType & {
  purchase_order_item_id: number
  sku_code: string
  sku_name: string
  invoice_po_item_ids: number[]
  base_unit_price: number
  discount_percentage: number
}

export interface InvoicePoMatchingsResponseType {
  po_number: string
  invoice_purchase_order_id: number
  matched_count: number
  total_count: number
  invoice_items: InvoicePoMatchingInvoiceItemType[]
  po_items: InvoicePoMatchingPoItemType[]
}

export interface InvoicePoMatchingDetailResponseType {
  po_number: string
  invoice_purchase_order_id: number
  matched_count: number
  total_count: number
  matched_items: {
    invoice_po_item_id: number
    po_item_id: number
    sku_code: string
    sku_name: string
    po: {
      unit_price: number
      discount_percentage: number
      nett_price: number
      tax_amount: number
      quantity: number
      tax_setting: boolean
      grn_quantity: number
      total_price: number
    }
    invoice: {
      unit_price: number
      quantity: number
      converter: number
      discount_percentage: number
      tax_setting: boolean
      surat_jalan_quantity: number
      unit_price_pcs: number
      quantity_pcs: number
      nett_price: number
      discount_amount: number
      tax_amount: number
      total_price: number
    }
    diff: {
      unit_quantity: number
      nett_unit_price: number
      tax_setting: boolean
      total_excl_tax: number
    }
    suggestive_remark: string
    suggestive_remark_detail: string
  }[]
  po_level: {
    materai_fee: number
    discount_po_level: number
    rounding: number
    po_remark: string
    po_remark_reason_id: number
    po_remark_reason: string
    discrepancy_cause: string
    po: {
      shipping_fee: number
      amount_excl_tax: number
      tax_total: number
      total_price: number
      total_quantity: number
    }
    invoice: {
      shipping_fee: number
      amount_excl_tax: number
      tax_total: number
      total_price: number
      total_quantity: number
    }
  }
}

export interface EditHistoryEntryType {
  id: number
  timestamp: string
  user_email: string
  action: 'EXTRACT_OCR_DATA' | 'EDIT_INVOICE'
  po_remarks: string
  po_changes: Record<string, { old: string | number; new: string | number }>
  item_changes: {
    sku_code: string
    sku_name: string
    po_item_id: number
    fields: Record<string, { old: string | number; new: string | number }>
  }[]
}

export interface GetInvoicePoEditHistoryResponseType {
  data: {
    po_number: string
    entries: EditHistoryEntryType[]
  }
}

export type InvoiceMatchingRejectedFieldType =
  | 'UNIT_PRICE'
  | 'DISCOUNT_PRICE'
  | 'ORDERED_QTY'
  | 'TAX_RATE'

export type InvoicePurchaseOrderMatchingPayloadType = {
  invoice_purchase_order_item_id: number
  purchase_order_item_id: number
  is_flagged: boolean
  rejection_reason: string
  rejected_fields: InvoiceMatchingRejectedFieldType[]
}

export interface RejectInvoicePayloadType {
  reason: string
  purchase_orders?: {
    invoice_purchase_order_id: number
    is_flagged_for_rejection: boolean
    matchings?: InvoicePurchaseOrderMatchingPayloadType[]
  }[]
  attachment_rejections?: {
    attachment_type: AttachmentRejectionTypeType
    reason_id: number
    reason_text: string
  }[]
}

export interface ReviseInvoicePayloadType {
  documents?: string[]
  attachments?: string[]
  purchase_orders?: {
    invoice_purchase_order_id: number
    is_flagged_for_rejection: boolean
    matchings?: InvoicePurchaseOrderMatchingPayloadType[]
  }[]
  attachment_rejections?: {
    attachment_type: AttachmentRejectionTypeType
    reason_id: number
    reason_text: string
  }[]
}

export interface SaveInvoicePoMatchingDetailPayloadType {
  matchings: {
    invoice_po_item_id: number
    po_item_id: number
    invoice_unit_price: number
    invoice_quantity: number
    invoice_converter: number
    invoice_discount_percentage: number
    invoice_tax_setting: boolean
    surat_jalan_quantity: number
  }[]
  shipping_fee_invoice: number
  materai_fee: number
  discount_po_level: number
  rounding: number
  po_remark: string
  po_remark_reason_id: number
  discrepancy_cause: DiscrepancyCauseType
}

export interface PODetailsInterface {
  po_number: string
  client_name: string
  vendor_name: string
  shipment_date: string
  payment_terms: string
  expired_date: string
  requested_by: string
  status: string
  location_name: string

  products: {
    sku_number: string
    product_name: string
    qty: number
    uom: string
    price: number
    discounted_price: number
    total_price: number
  }[]
  notes: string
  sub_total: number
  total_discount: number
  tax: string
  tax_amount: number
  shipping_cost: number
  total: number
  activities: {
    activity_name: string
    start_date: string
    end_date: string
    duration: string
    user_name: string
    attachment_url: string
  }[]
}

export interface GetPODetailResponseInterface {
  data: PODetailsInterface
}

export interface GetStatusActiveInvoicesResponseInterface {
  data: StatusActiveInvoicesInterface
}

export interface StatusActiveInvoicesInterface {
  pending: boolean
  approved: boolean
  need_revised: boolean
  reviewed: boolean
}

export interface ChangeDueDateDataInterface {
  due_date: string
  reason: string
}

export interface GetInvoiceReasonsResponseInterface {
  data: InvoiceReasonsType[]
}

export type InvoiceReasonsType = {
  id: number
  reason: string
}

export const getVendorInvoicesAPI = (params: GetVendorInvoiceQueryType) =>
  AxiosInstance.Get<VendorInvoiceResponseInterface, true>({
    url: '/commercial/internal/:version/invoices',
    version: 'v1',
    params,
  })

export const getInvoiceDetailsAPI = (invoiceId: string) =>
  AxiosInstance.Get<GetInvoiceDetailsResponseInterface, true>({
    url: '/commercial/internal/:version/invoices/:invoiceId',
    version: 'v1',
    urlParams: { invoiceId },
  })

export const getInvoiceActivitiesAPI = (invoiceId: string | number) =>
  AxiosInstance.Get<{ data: InvoiceActivityItemType[] }, true>({
    url: '/commercial/internal/:version/invoices/:invoiceId/activities',
    version: 'v1',
    urlParams: { invoiceId },
  })

export const getPODetailsAPI = (poNumber: string) =>
  AxiosInstance.Get<GetPODetailResponseInterface, true>({
    url: '/commercial/internal/:version/vendor-invoice/po-details/:poNumber',
    version: 'v1',
    urlParams: { poNumber },
  })

export const COMMERCIAL_FEATURE_FLAG_PO_FULLY_MIGRATED_MIN_ID = 'FF_PO_FULLY_MIGRATED_MIN_ID'

export type CommercialPoFullyMigratedFeatureFlagsType = {
  [COMMERCIAL_FEATURE_FLAG_PO_FULLY_MIGRATED_MIN_ID]: string
}

export const getCommercialPoFullyMigratedFeatureFlagApi = () =>
  AxiosInstance.Get<{ data: CommercialPoFullyMigratedFeatureFlagsType }, true>({
    url: '/commercial/internal/:version/feature-flags?flags=FF_PO_FULLY_MIGRATED_MIN_ID',
    version: 'v1',
  })

export const getStatusActiveInvoicesAPI = () =>
  AxiosInstance.Get<GetStatusActiveInvoicesResponseInterface, true>({
    url: '/commercial/internal/:version/invoices/status-active',
    version: 'v1',
  })

export const putChangeDueDateAPI = (id: number, data: ChangeDueDateDataInterface) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:id/due-date',
    version: 'v1',
    urlParams: { id },
    data,
  })

export const putInvoiceStatusToReviewedAPI = (id: number) =>
  AxiosInstance.Put({
    url: `/commercial/internal/:version/invoices/:id/reviewal`,
    version: 'v1',
    urlParams: { id },
  })

export const putInvoiceStatusToApprovedAPI = (id: number) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:id/approval',
    version: 'v1',
    urlParams: { id },
  })

export const putMarkAsPaidAPI = (id: number, data?: FormData) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:id/payment',
    version: 'v1',
    urlParams: { id },
    data,
  })

export const getReasonOptionsAPI = (type: string) =>
  AxiosInstance.Get<GetInvoiceReasonsResponseInterface, true>({
    url: '/commercial/internal/:version/reason/:type',
    version: 'v1',
    urlParams: { type },
  })

export const putRejectInvoiceAPI = (id: number, data: RejectInvoicePayloadType) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:id/rejection',
    version: 'v1',
    urlParams: { id },
    data,
  })

export const putReviseInvoiceAPI = (id: number, data: ReviseInvoicePayloadType) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:id/revision',
    version: 'v1',
    urlParams: { id },
    data,
  })

export const getInvoicePoMatchingsAPI = (invoiceId: number, invoicePurchaseOrderId: number) =>
  AxiosInstance.Get<{ data: InvoicePoMatchingsResponseType }, true>({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/items/matchings',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId },
  })

export const saveInvoicePoMatchingsAPI = (
  invoiceId: number,
  invoicePurchaseOrderId: number,
  data: { matchings: { invoice_item_id: number; po_item_ids: number[] }[] },
) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/items/matchings',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId },
    data,
  })

export const addManualInvoiceItemsAPI = (
  invoiceId: number,
  invoicePurchaseOrderId: number,
  data: {
    items: {
      vendor_sku_code: string
      vendor_sku_name: string
      invoice_unit_price: number
      invoice_quantity: number
      invoice_converter: number
      invoice_discount_percentage: number
      invoice_tax_setting: boolean
    }[]
  },
) =>
  AxiosInstance.Post({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/items/manual',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId },
    data,
  })

export const deleteInvoicePoItemAPI = (
  invoiceId: number,
  invoicePurchaseOrderId: number,
  invoicePoItemId: number,
) =>
  AxiosInstance.Delete({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/items/:invoicePoItemId',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId, invoicePoItemId },
  })

export const getInvoicePoMatchingDetailAPI = (invoiceId: number, invoicePurchaseOrderId: number) =>
  AxiosInstance.Get<{ data: InvoicePoMatchingDetailResponseType }, true>({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/items/matchings/detail',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId },
  })

export const saveInvoicePoMatchingDetailAPI = (
  invoiceId: number,
  invoicePurchaseOrderId: number,
  data: SaveInvoicePoMatchingDetailPayloadType,
) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/items/matchings/detail',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId },
    data,
  })

export const getInvoicePoEditHistoryAPI = (invoiceId: number, invoicePurchaseOrderId: number) =>
  AxiosInstance.Get<GetInvoicePoEditHistoryResponseType, true>({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/edit-history',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId },
  })

export const triggerInvoicePoSkuMatchingAPI = (invoiceId: number, invoicePurchaseOrderId: number) =>
  AxiosInstance.Post<TriggerSkuMatchingApiResponseType, true>({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/sku-matching',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId },
    data: {},
  })

export const getInvoicePoSkuMatchingResultAPI = (
  invoiceId: number,
  invoicePurchaseOrderId: number,
  requestId: string,
) =>
  AxiosInstance.Get<GetSkuMatchingResultApiResponseType, true>({
    url: '/commercial/internal/:version/invoices/:invoiceId/po/:invoicePurchaseOrderId/sku-matching/:requestId',
    version: 'v1',
    urlParams: { invoiceId, invoicePurchaseOrderId, requestId },
  })

export const getDocFileUrlAPI = (
  fileUrl: string,
  type: string,
  downloadTypeId: InvoiceFileDownloadTypeIdType = 'seller',
) =>
  AxiosInstance.Get<Blob, true>({
    url: `/internal/:version/${downloadTypeId}/download`,
    version: 'v1',
    params: { fileUrl },
    config: {
      baseURL: process.env.REACT_APP_BASE_URL_API_FILE_SERVICE,
      responseType: 'blob',
      transformResponse: (data) => new Blob([data], { type }),
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'X-Service-Name': 'astro-app-web-admin',
      },
    },
  })

export type UpdateInvoiceDetailsPayloadType = {
  invoice_number: string
  issued_date: string
  tax_invoice_number?: string
  tax_invoice_date?: string
}

export const putUpdateInvoiceDetailsAPI = (
  invoiceId: number,
  data: UpdateInvoiceDetailsPayloadType,
) =>
  AxiosInstance.Put({
    url: '/commercial/internal/:version/invoices/:id',
    version: 'v1',
    urlParams: { id: invoiceId },
    data,
  })
